<script>
    import dressing from "@intellectability/platform-common";
    // common Svelte components
    import {
        ExportButtons,
        FlexList,
        Icon,
        SuperFilter,
        SuperFilterRangeSlider, userSecurity
    } from "@intellectability/platform-common/sv";
    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";

    import {
        Breadcrumb,
        BreadcrumbItem,
        Dropdown,
        DropdownItem,
        DropdownMenu,
        DropdownToggle,
        Label
    } from "@sveltestrap/sveltestrap";

    import {ACTIONS} from "@intellectability/platform-common/constants";

    const {PERSONS_SERVED_LIST, PERSON_DETAILS_VIEW} = ACTIONS;

    let hasAccess = actionCheck(userSecurity.userActions);
    let loading = $state(true);
    let isLoggedIn = userSecurity.loggedIn;

    // flexlist data
    let personsServed = $state.raw([]);

    // for dropdowns
    let providerAgenciesOptions = $state.raw([]);
    let caseWorkersOptions = $state.raw([]);
    let providerServicesOptions = $state.raw([]);
    let regionsOptions = $state.raw([]);
    let statusesOptions = $state.raw([
        "Active", "Inactive", "Deleted", "Deceased"
    ]);
    let waiversOptions = $state.raw([]);
    let planListsOptions = $state.raw([]);
    let dateOfBirthMonthsOptions = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
    ];

    // Superfilter
    let selectedProvider = $state({});
    let selectedCaseWorkerName = $state("");
    let selectedProviderService = $state("");
    let selectedHealthCareLevelRange = $state([1, 6]);
    let selectedAgeRange = $state([0, 125]);

    // Flexlist filters
    let consumerID = $state("");
    let selectedAge = $state("");
    let selectedDOBMonth = $state("");
    let selectedFirstName = $state("");
    let selectedLastName = $state("");
    let selectedHealthCareLevel = $state("");
    let selectedGender = $state("");
    let selectedRegion = $state("");
    let selectedStatus = $state("Active");
    let selectedWaiver = $state("");
    let selectedPlanList = $state("");
    let selectedCounty = $state("");

    // pagination
    let pagination = $state({
        show: true,
        page: 1,
        limit: 10,
        recordsFiltered: null,
        recordsTotal: null,
        pageLengthMenu: [50, 100, 500]
    });

    // sort
    let sort = $state({
        field: "last_name",
        order: "asc"
    });

    const persons_served_list_columns = $derived([
        {
            name: "First Name",
            field: "cons_fname",
            sortable: true,
            sortedBy: sort.field === "cons_fname" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedFirstName = value;
            }
        },
        {
            name: "Last Name",
            field: "cons_lname",
            sortable: true,
            sortedBy: sort.field === "cons_lname" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedLastName = value;
            }
        },
        {
            name: "HCL",
            field: "hrst_hc_lvl",
            sortable: true,
            sortedBy: sort.field === "hrst_hc_lvl" ? sort.order : "",
            filterType: "select",
            filterOptions: [1, 2, 3, 4, 5, 6],
            onfilter: function (value) {
                selectedHealthCareLevel = value;
            }
        },
        {
            name: "Age",
            field: "cons_age",
            sortable: true,
            sortedBy: sort.field === "cons_age" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedAge = value;
            }
        },
        {
            name: "Gender",
            field: "cons_sex",
            sortable: true,
            sortedBy: sort.field === "cons_sex" ? sort.order : "",
            filterType: "select",
            filterOptions: ["Male", "Female"],
            onfilter: function (value) {
                selectedGender = value;
            }
        },
        {
            name: "Region",
            field: "cons_region",
            sortable: true,
            sortedBy: sort.field === "cons_region" ? sort.order : "",
            filterType: "select",
            filterOptions: regionsOptions,
            onfilter: function (value) {
                selectedRegion = value;
            }
        },
        {
            name: "Status",
            field: "cons_status",
            sortable: true,
            sortedBy: sort.field === "cons_status" ? sort.order : "",
            filterType: "select",
            filterOptions: statusesOptions,
            onfilter: function (value) {
                selectedStatus = value;
            }
        },
        {
            name: "Waiver",
            field: "cons_waiver",
            sortable: true,
            sortedBy: sort.field === "cons_waiver" ? sort.order : "",
            filterType: "select",
            filterOptions: waiversOptions,
            onfilter: function (value) {
                selectedWaiver = value;
            }
        },
        {
            name: "Plan List",
            field: "cons_plan_list",
            sortable: true,
            sortedBy: sort.field === "cons_plan_list" ? sort.order : "",
            filterType: "select",
            filterOptions: planListsOptions,
            onfilter: function (value) {
                selectedPlanList = value;
            }
        },
        {
            name: "DOB Month",
            field: "cons_birth_month",
            sortable: true,
            sortedBy: sort.field === "cons_birth_month" ? sort.order : "",
            filterType: "select",
            filterOptions: dateOfBirthMonthsOptions,
            onfilter: function (value) {
                selectedDOBMonth = value;
            }
        },
        {
            name: "County",
            field: "cons_county",
            sortable: true,
            sortedBy: sort.field === "cons_county" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedCounty = value;
            }
        }
    ]);

    $effect(function () {

        if (isLoggedIn && !hasAccess(PERSONS_SERVED_LIST)) {
            const err = create_security_error("PersonsServedList.svelte", "PersonsServedList", PERSONS_SERVED_LIST);
            dressing.api.report_security_error(err).then(() => dressing.api.logout());
        }

        if (isLoggedIn) {
            const needed_initial_data = [
                dressing.get("/providers").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const {providers: p} = data;
                        if (Array.isArray(p) && p.length > 0) {
                            providerAgenciesOptions = p.map(function (p) {
                                return {
                                    name: p.prv_name,
                                    value: p.prv_id
                                };
                            });
                        }
                        return success;
                    }
                })
                // dressing.get("/providers/statuses").then(({data: res}) => {
                //     const {success, data} = res;
                //     if (success === true) {
                //         const {statuses} = data;
                //         if (Array.isArray(statuses) && statuses.length > 0) {
                //             providerStatuses = statuses;
                //         }
                //         return success;
                //     }
                // }),
                // dressing.get("/providers/services").then(({data: res}) => {
                //     const {success, data} = res;
                //     if (success === true) {
                //         const {services} = data;
                //         if (Array.isArray(services) && services.length > 0) {
                //             providerServices = services;
                //         }
                //         return success;
                //     }
                // })
            ];

            // optionally fetch parent provider data if needed per business rules
            // if (applicable_business_rules.some(function (rule) {
            //     return rule.rule_name === "show_parent_provider_in_superfilter";
            // })) {
            //     needed_initial_data.push(dressing.api.fetch_business_rule_data("show_parent_provider_in_superfilter")
            //         .then(({data: res}) => {
            //             const {success, data} = res;
            //             if (success === true) {
            //                 parentProviders = data;
            //             }
            //             return success;
            //         }));
            // }

            Promise.allSettled(needed_initial_data)
                .then(function (resolved) {
                    const result = resolved.every(r => r.value === true);
                    if (result) {
                        return loading = false;
                    }
                    else {
                        toast.error("Error loading initial data");
                    }
                });
        }
    });

    $effect(function filter() {
        if (isLoggedIn) {

            const filter_query = {
                cons_fname: selectedFirstName || null,
                cons_lname: selectedLastName || null,
                hrst_hc_lvl: selectedHealthCareLevel || null,
                cons_age: selectedAge || null,
                cons_sex: selectedGender || null,
                cons_region: selectedRegion || null,
                cons_status: selectedStatus || null,
                cons_waiver: selectedWaiver || null,
                cons_plan_list: selectedPlanList || null,
                cons_birth_month: selectedDOBMonth || null,
                cons_county: selectedCounty || null,
                provider: selectedProvider.value || null
            };

            const sort_query = {
                sort: sort.field || null,
                order: sort.order || null
            };

            const page_query = {
                page: pagination.page || null,
                limit: pagination.limit || null
            };

            dressing.get("/people", {
                params: Object.assign(filter_query, {...sort_query, ...page_query})
            }).then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {

                    const {persons_served: p} = data;
                    if (Array.isArray(p)) {
                        personsServed = p;
                        pagination.recordsFiltered = data.recordsFiltered;
                        pagination.recordsTotal = data.recordsTotal;
                    }
                }
            });
        }
    });

    function row_selected(row) {
        if (hasAccess(PERSON_DETAILS_VIEW)) {
            window.location.href = `/index.php?action=${PERSON_DETAILS_VIEW}&pid=${row.cons_id}`;
        }
        else {
            dressing.api.report_security_error(
                create_security_error("PersonsServedList.svelte", "PersonsServedList", PERSON_DETAILS_VIEW));
        }
    }

</script>

<!-- Breadcrumbs -->
<Breadcrumb divider={" "} class="breadcrumbs mb-3">
    <BreadcrumbItem>
        <a href="/index.php?action=30000">
            <Icon icon="home" class="mr-1"/>
            PERSONS SERVED
        </a>
    </BreadcrumbItem>
    <BreadcrumbItem class="mx-3">
        <Icon icon="chevron-right"/>
    </BreadcrumbItem>
    <BreadcrumbItem>
        LIST
    </BreadcrumbItem>
</Breadcrumb>
<h1>
    <Icon icon="home"/>
    Persons Served
</h1>
<!-- Super Filter -->
<div class="mb-4">
    <SuperFilter classes="row px-0 mx-0" onreset={() => {
                selectedProvider = "";
                selectedCaseWorkerName = "";
                selectedProviderService = "";
                selectedHealthCareLevelRange = [1, 6];
                selectedAgeRange = [0, 125];
            }}>
        <div class="col-12 col-md-2">
            <Label class="w-100">
                Provider Agency
                <Dropdown direction="down">
                    <DropdownToggle color="transparent" class="w-100" caret>
                                    <span>
                                        {selectedProvider.name || "All"}
                                    </span>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onclick={() => selectedProvider = {}}>
                            All
                        </DropdownItem>
                        {#each providerAgenciesOptions as p}
                            <DropdownItem
                                    class={selectedProvider.value === p.value ? "active" : ""}
                                    onclick={() => selectedProvider = p}>
                                {p.name}
                            </DropdownItem>
                        {/each}
                    </DropdownMenu>
                </Dropdown>
            </Label>
        </div>
        <div class="col-12 col-md-2">
            <Label class="w-100">
                Caseworker
                <Dropdown direction="down">
                    <DropdownToggle color="transparent" class="w-100" caret>
                                    <span>
                                        {selectedCaseWorkerName || "All"}
                                    </span>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onclick={() => selectedCaseWorkerName = ""}>
                            All
                        </DropdownItem>
                        {#each caseWorkersOptions as {name}}
                            <DropdownItem
                                    class={selectedCaseWorkerName === name ? "active" : ""}
                                    onclick={() => selectedCaseWorkerName = name}>
                                {name}
                            </DropdownItem>
                        {/each}
                    </DropdownMenu>
                </Dropdown>
            </Label>
        </div>
        <div class="col-12 col-md-2">
            <Label class="w-100">
                Caseworker
                <Dropdown direction="down">
                    <DropdownToggle color="transparent" class="w-100" caret>
                                    <span>
                                        {selectedProviderService || "All"}
                                    </span>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onclick={() => selectedProviderService = ""}>
                            All
                        </DropdownItem>
                        {#each providerServicesOptions as {name}}
                            <DropdownItem
                                    class={selectedProviderService === name ? "active" : ""}
                                    onclick={() => selectedProviderService = name}>
                                {name}
                            </DropdownItem>
                        {/each}
                    </DropdownMenu>
                </Dropdown>
            </Label>
        </div>
        <div class="col-12 col-md">
            <SuperFilterRangeSlider
                    bind:currentRange={selectedHealthCareLevelRange}
                    min="1" max="6" label="Health Care Level"/>
        </div>
        <div class="col-12 col-md">
            <SuperFilterRangeSlider bind:currentRange={selectedAgeRange}
                                    min={0} max={125}
                                    label="Ages (0 - 125)"/>
        </div>
    </SuperFilter>
</div>
<!-- Export Buttons -->
<div class="d-flex w-100 justify-content-end mb-3">
    <ExportButtons show={["excel", "pdf"]}
                   href={`ajax/default/ajax.download_list.php?list=${PERSONS_SERVED_LIST}`}/>
</div>

<!-- TABLE -->
<FlexList
        columns={persons_served_list_columns}
        data={personsServed}
        hoverable={hasAccess(PERSONS_SERVED_LIST)}
        onselection={row_selected}
        loading={loading}
        onpagechange={(page) => pagination.page = page}
        onsort={(field, order) => {sort = {field, order};}}
        pagerConfig={pagination}
/>